/**
 * Pure helpers for the audit explorer and the Security page: time ranges, alert tones and
 * Needs you rows, and the demo data ?mock=1 runs on. Tested in model.test.ts.
 */
import { DETECTION_DEFAULTS, HUB_AUDIT_MACHINE, RETENTION_DEFAULTS, SECURITY_RULE_LABEL, auditCsvHeader, auditCsvRow, auditSiemRecord, compareAuditNewestFirst, runAuditQuery } from '@orgus/protocol';
import type {
  AuditEntry,
  AuditExportFormat,
  AuditQuery,
  AuditQueryResult,
  ChainVerifyResult,
  Id,
  Machine,
  RestoreRecord,
  RetentionSettings,
  RetentionState,
  SecurityAlert,
  SecurityState,
} from '@orgus/protocol';
import type { NeedItem } from '../overview/needsYou.ts';

export function auditActorLabel(e: AuditEntry): string {
  return typeof e.params?.onBehalfOf === 'string' && ['orgus', 'policy', 'system'].includes(e.actor)
    ? `Orgus, on behalf of ${e.params.onBehalfOf}` : e.actor;
}

export type TimeRange = '24h' | '7d' | '30d' | 'all';
const RANGE_MS: Record<Exclude<TimeRange, 'all'>, number> = { '24h': 86_400_000, '7d': 7 * 86_400_000, '30d': 30 * 86_400_000 };

export const TIME_RANGE_LABEL: Record<TimeRange, string> = { '24h': '24 h', '7d': '7 days', '30d': '30 days', all: 'All time' };

export function rangeFrom(r: TimeRange, now: number): number | undefined {
  return r === 'all' ? undefined : now - RANGE_MS[r];
}

export function isTimeRange(v: string | null): v is TimeRange {
  return v === '24h' || v === '7d' || v === '30d' || v === 'all';
}

export function machineLabel(machines: Record<Id, Machine>, id: Id): string {
  return machines[id]?.name ?? (id === HUB_AUDIT_MACHINE ? 'Hub' : 'Removed machine');
}

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

export type SecurityNeedKind = 'securityCritical' | 'securityWarning';

export interface SecurityNeedItem extends Omit<NeedItem, 'kind'> {
  kind: SecurityNeedKind;
}

/** Open alerts as Needs you rows, critical first. */
export function securityNeeds(alerts: readonly SecurityAlert[], machines: Record<Id, Machine>): SecurityNeedItem[] {
  return alerts
    .filter((a) => a.status === 'open')
    .sort((a, b) => (a.severity === b.severity ? b.lastAt - a.lastAt : a.severity === 'critical' ? -1 : 1))
    .map((a) => ({
      key: `alert:${a.id}`,
      kind: a.severity === 'critical' ? 'securityCritical' : 'securityWarning',
      title: a.title,
      detail: a.count > 1 ? `${SECURITY_RULE_LABEL[a.rule].title}, ${a.count} times` : SECURITY_RULE_LABEL[a.rule].title,
      where: a.machineId ? machineLabel(machines, a.machineId) : a.actor ?? 'Hub',
      at: a.lastAt,
      to: { path: `/security?alert=${encodeURIComponent(a.id)}` },
      verb: 'Review',
    }));
}

// ---------------------------------------------------------------------------
// Export without a hub (demo mode): the same bytes the hub would send
// ---------------------------------------------------------------------------

export function exportText(entries: readonly AuditEntry[], format: AuditExportFormat, machines: Record<Id, Machine>): string {
  const sorted = [...entries].sort((a, b) => b.at - a.at);
  if (format === 'csv') return `﻿${auditCsvHeader()}${sorted.map((e) => auditCsvRow(e, machineLabel(machines, e.machineId))).join('')}`;
  return sorted.map((e) => `${JSON.stringify(auditSiemRecord(e, machineLabel(machines, e.machineId)))}\n`).join('');
}

// ---------------------------------------------------------------------------
// Demo data
// ---------------------------------------------------------------------------

const H = 3_600_000;

/** Older entries the demo pretends are archived, so archive search and retention have something to show. */
export function mockArchivedEntries(now: number): AuditEntry[] {
  const out: AuditEntry[] = [];
  for (let i = 0; i < 24; i++) {
    out.push({
      id: `a-archived-${i}`,
      at: now - (95 + i * 4) * 24 * H,
      actor: i % 3 ? 'owner' : 'agent:nightly',
      action: i % 4 ? 'job.run' : 'session.start',
      target: i % 4 ? `npm test -- shard ${i}` : 'billing-service: Nightly dependency upgrade',
      machineId: 'm-build',
      params: {},
      result: i % 7 ? 'ok' : 'error',
      ...(i % 7 ? {} : { message: 'Exited with code 1' }),
    });
  }
  return out;
}

export function mockRetention(now: number, live: readonly AuditEntry[]): RetentionState {
  const archived = mockArchivedEntries(now);
  const seg = (k: number, from: number, to: number, count: number) => ({
    file: `audit-${new Date(from).toISOString().replace(/[-:]/g, '').slice(0, 15)}Z-${new Date(to).toISOString().replace(/[-:]/g, '').slice(0, 15)}Z.jsonl.gz`,
    from,
    to,
    count,
    firstId: `a-archived-${k}`,
    lastId: `a-archived-${k + count - 1}`,
    startHash: '9f1c'.padEnd(64, '0'),
    endHash: '4ab2'.padEnd(64, '7'),
    sha256: 'c0de'.padEnd(64, '1'),
    bytes: 1_840 * count,
    archivedAt: to + 90 * 24 * H,
    by: 'system:retention',
  });
  const oldest = archived[archived.length - 1]!.at;
  const newest = archived[0]!.at;
  return {
    settings: { ...RETENTION_DEFAULTS },
    live: { entries: live.length, oldest: live.length ? Math.min(...live.map((e) => e.at)) : null, bytes: live.length * 420 },
    archive: { segments: [seg(12, oldest, archived[12]!.at, 12), seg(0, archived[11]!.at, newest, 12)], entries: archived.length, pruned: 0, oldest },
    lastRun: { at: now - 3 * H, by: 'system:retention', archived: 0, pruned: 0, ok: true, message: 'No entries are old enough to archive.' },
    nextRunAt: now + 3 * H,
  };
}

/**
 * GET /api/audit/verify as a ChainVerifyResult, whatever served it. A hub without audit-ops and
 * the Workers control plane answer the older { ok, checked } or { ok: false, brokenAt, reason }.
 */
export function normalizeVerify(raw: unknown, now: number): ChainVerifyResult {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  if (typeof r.summary === 'string' && r.live && typeof r.live === 'object' && r.archive && typeof r.archive === 'object') return r as unknown as ChainVerifyResult;
  const ok = r.ok === true;
  const checked = typeof r.checked === 'number' ? r.checked : typeof r.entries === 'number' ? r.entries : 0;
  const line = typeof r.brokenAt === 'number' ? r.brokenAt : r.brokenAt && typeof r.brokenAt === 'object' && typeof (r.brokenAt as { line?: unknown }).line === 'number' ? (r.brokenAt as { line: number }).line : 0;
  const reason =
    typeof r.reason === 'string' ? r.reason : r.brokenAt && typeof r.brokenAt === 'object' && typeof (r.brokenAt as { reason?: unknown }).reason === 'string' ? (r.brokenAt as { reason: string }).reason : 'The server did not say why.';
  const n = (x: number) => `${x.toLocaleString('en-US')} ${x === 1 ? 'entry' : 'entries'}`;
  return {
    ok,
    checkedAt: now,
    verified: ok ? checked : Math.max(0, line - 1),
    live: { entries: checked, chained: checked, legacy: 0, head: typeof r.head === 'string' ? r.head : null, anchorMatches: null },
    archive: { segments: 0, entries: 0, prunedSegments: 0, prunedEntries: 0 },
    ...(ok ? {} : { brokenAt: { where: 'live' as const, file: 'audit log', line, id: null, reason } }),
    summary: ok ? `Chain intact: ${n(checked)} verified.` : `Broken${line ? ` at entry ${line}` : ''}: ${reason}`,
  };
}

/**
 * The explorer over the older GET /api/audit list ({ entries, more }), for a server without
 * /api/audit/query (the Workers control plane): the newest 1,000 entries, filtered here.
 */
export function legacyQueryResult(entries: readonly AuditEntry[], q: AuditQuery): AuditQueryResult {
  const r = runAuditQuery(entries, q);
  return { ...r, scanned: entries.length, archive: { searched: false, entries: 0, unreadable: [] } };
}

/** Rows after a quiet refresh: page one's entries in front of everything already loaded, once each, in page order. */
export function mergeRows(fresh: readonly AuditEntry[], loaded: readonly AuditEntry[]): AuditEntry[] {
  const seen = new Set<string>();
  const out: AuditEntry[] = [];
  for (const e of [...fresh, ...loaded]) {
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    out.push(e);
  }
  return out.sort(compareAuditNewestFirst);
}

/** Why the server would refuse these retention settings, or null. Mirrors the hub's parseRetention. */
export function retentionProblem(s: RetentionSettings): string | null {
  if (s.liveDays != null && s.archiveDays != null && s.archiveDays <= s.liveDays) {
    return 'Keep archived segments longer than entries stay in the live log, or entries would be deleted as soon as they are archived.';
  }
  return null;
}

/**
 * Live entries the next run archives and deletes at once: the run moves every entry older than
 * liveDays into one new segment, and deletes that segment straight away when even its newest
 * entry is older than archiveDays. `batch` is what the next run would archive (count, newest time).
 */
export function liveDeletedBy(batch: { total: number; newest: number | null } | null, s: RetentionSettings, now: number): number {
  if (!batch || !batch.total || batch.newest == null || s.liveDays == null || s.archiveDays == null) return 0;
  return batch.newest < now - s.archiveDays * 86_400_000 ? batch.total : 0;
}

/** The Security page's line about the last restore into this hub folder. */
export function restoreSummary(r: RestoreRecord): { tone: 'passed' | 'waiting'; title: string; detail: string } {
  const lost = r.replaced ? `The log it replaced had ${r.replaced.entries.toLocaleString('en-US')} ${r.replaced.entries === 1 ? 'entry' : 'entries'}${r.replaced.intact ? '' : ' and did not verify'}; entries written after the backup are only in ${r.previous ?? 'the replaced folder'}.` : 'There was no hub folder to replace.';
  const caution = r.force || !r.chainOk;
  return {
    tone: caution ? 'waiting' : 'passed',
    title: caution ? 'Restored over a chain that did not verify' : 'Restored from a backup',
    detail: `Backup taken ${new Date(r.backupCreatedAt).toISOString().slice(0, 16).replace('T', ' ')} UTC on ${r.backupHost}. ${lost} Recorded in the audit log as Restore hub from backup.`,
  };
}

export function mockVerify(now: number, live: number, archived: number): ChainVerifyResult {
  return {
    ok: true,
    checkedAt: now,
    verified: live + archived,
    live: { entries: live, chained: live, legacy: 0, head: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855', anchorMatches: true },
    archive: { segments: 2, entries: archived, prunedSegments: 0, prunedEntries: 0 },
    summary: `Chain intact: ${live + archived} entries verified, 2 archived segments.`,
  };
}

export function mockSecurity(machines: Machine[], now: number): SecurityState {
  const m = (i: number) => machines[i % Math.max(1, machines.length)];
  const studio = m(0);
  const build = m(2) ?? m(1);
  const alerts: SecurityAlert[] = [
    {
      id: 'al-demo-two-places',
      rule: 'credential-two-places',
      severity: 'critical',
      title: `${build?.name ?? 'build-01'}'s credentials are in use from two places`,
      detail: 'Connections from 10.0.4.17 and 203.0.113.40 replaced each other 3 times in 300 seconds. Two agents are running with one identity, or a copy of it.',
      next: `Revoke ${build?.name ?? 'build-01'} in Security now, then re-enrol the real machine with a new join token.`,
      at: now - 7 * 60_000,
      lastAt: now - 2 * 60_000,
      count: 3,
      machineId: build?.id ?? null,
      actor: null,
      status: 'open',
      evidence: { addresses: ['10.0.4.17', '203.0.113.40'], swaps: 3, windowSec: 300 },
    },
    {
      id: 'al-demo-new-net',
      rule: 'new-address',
      severity: 'warning',
      title: `${studio?.name ?? 'studio'} connected from a new network`,
      detail: 'It connected from 100.71.3.9 (100.71.3.0/24). Before, it used only 192.168.1.20.',
      next: `If ${studio?.name ?? 'studio'} did not move to another network, revoke it in Security and re-enrol it with a new join token.`,
      at: now - 48 * 60_000,
      lastAt: now - 48 * 60_000,
      count: 1,
      machineId: studio?.id ?? null,
      actor: null,
      status: 'open',
      evidence: { address: '100.71.3.9', network: '100.71.3.0/24' },
    },
    {
      id: 'al-demo-denials',
      rule: 'repeated-denials',
      severity: 'warning',
      title: '5 actions by agent:triage refused in 10 min',
      detail: 'Latest: Refused by trust: Orgus is frozen: every action that starts or changes work is refused.',
      next: 'Check what agent:triage is trying to do in Audit. If it is not expected, revoke its access or freeze all writes.',
      at: now - 26 * H,
      lastAt: now - 26 * H,
      count: 1,
      machineId: null,
      actor: 'agent:triage',
      status: 'acknowledged',
      ack: { by: 'owner', at: now - 25 * H, note: 'Expected during the freeze drill.' },
      evidence: {},
    },
  ];
  return {
    alerts,
    rules: structuredClone(DETECTION_DEFAULTS),
    format: { versions: { hub: 1 }, known: { hub: 1 }, history: [{ id: 'hub-0001-format-file', scope: 'hub', to: 1, at: now - 30 * 24 * H, description: 'Record the folder format in format.json.' }] },
    lastBackup: { at: now - 20 * H, mode: 'live', files: 14, bytes: 412_000, file: '~/.orgus/backups/hub-20261016T060000Z.orgus-backup', by: 'cli' },
  };
}
